import { createApp } from '@backstage/frontend-defaults';
import catalogPlugin from '@backstage/plugin-catalog/alpha';
import scaffolderPlugin from '@backstage/plugin-scaffolder/alpha';
import techDocsPlugin from '@backstage/plugin-techdocs/alpha';
import { techDocsMermaidAddonModule } from 'backstage-plugin-techdocs-addon-mermaid';
import notificationsPlugin from '@backstage/plugin-notifications/alpha';
import signalsPlugin from '@backstage/plugin-signals/alpha';
import rbacPlugin from '@backstage-community/plugin-rbac/alpha';
import searchPlugin from '@backstage/plugin-search/alpha';
import userSettingsPlugin from '@backstage/plugin-user-settings/alpha';
import apiDocsPlugin from '@backstage/plugin-api-docs/alpha';
import catalogImportPlugin from '@backstage/plugin-catalog-import/alpha';
import catalogGraphPlugin from '@backstage/plugin-catalog-graph/alpha';
import githubPullRequestsBoardPlugin from '@backstage-community/plugin-github-pull-requests-board/alpha';
import githubActionsPlugin from '@backstage-community/plugin-github-actions/alpha';
import githubIssuesPlugin from '@backstage-community/plugin-github-issues/alpha';
import announcementsPlugin from '@backstage-community/plugin-announcements/alpha';
import homePlugin from '@backstage/plugin-home/alpha';
import techRadarPlugin from '@backstage-community/plugin-tech-radar/alpha';
import bookmarksPlugin from '@backstage-community/plugin-bookmarks/alpha';
import explorePlugin from '@backstage-community/plugin-explore/alpha';
import entityValidationPlugin from '@backstage-community/plugin-entity-validation/alpha';
import cicdStatisticsPlugin from '@backstage-community/plugin-cicd-statistics/alpha';
import cicdStatisticsGithubModule from '@backstage-community/plugin-cicd-statistics-module-github/alpha';
import todoPlugin from '@backstage-community/plugin-todo/alpha';
import npmPlugin from '@backstage-community/plugin-npm/alpha';
import linguistPlugin from '@backstage-community/plugin-linguist/alpha';
import readmePlugin from '@axis-backstage/plugin-readme/alpha';
import toolboxPlugin from '@drodil/backstage-plugin-toolbox/alpha';
import wheelOfNamesPlugin from '@backstage-community/plugin-wheel-of-names/alpha';
import entityFeedbackPlugin from '@backstage-community/plugin-entity-feedback/alpha';
import devToolsPlugin from '@backstage/plugin-devtools/alpha';
import managePlugin from '@backstage-community/plugin-manage';
import techInsightsPlugin from '@backstage-community/plugin-tech-insights/alpha';
import qetaPlugin from '@drodil/backstage-plugin-qeta/alpha';
import { navModule } from './modules/nav';
import costInsightsPlugin from '@backstage-community/plugin-cost-insights/alpha';
import { secretsModule } from './modules/secrets';
import { signInModule } from './modules/sign-in';
import { awsEcsModule } from './modules/aws-ecs';
import { awsEcrModule } from './modules/aws-ecr';
import { awsCodePipelineModule } from './modules/aws-codepipeline';
import { awsCodeBuildModule } from './modules/aws-codebuild';
import { awsSecurityHubModule } from './modules/aws-securityhub';
import { awsCostInsightsModule } from './modules/aws-cost-insights';
import { homeModule } from './modules/home';
import { techRadarModule } from './modules/tech-radar';

// IMPORTANT — Frontend System feature registration:
// Every plugin that exposes an apiRef other plugins might consume MUST be
// added here. The RBAC plugin (and scaffolder) consume the notifications
// API for toast messages; without notificationsPlugin in this array, any
// page that triggers a toast throws `NotImplementedError: No implementation
// available for apiRef{plugin.notifications.service}` at render time.
//
// The notifications and signals BACKENDS are wired separately in
// packages/backend/src/index.ts. Signals delivers real-time updates over
// a WebSocket; without signalsPlugin in features, the backend logs auth
// failures on every page load (the frontend never subscribes).
//
// 2026-06-01 audit: added 5 more frontend plugins after Playwright audit
// found their absence caused:
//   - searchPlugin       → sidebar Search button threw NotImplementedError
//                          on plugin.search.queryservice apiRef
//   - userSettingsPlugin → no user avatar/sign-out UI in sidebar (the
//                          UserSettingsSignInAvatar component in
//                          Sidebar.tsx silently rendered nothing) + /settings 404
//   - apiDocsPlugin      → /api-docs 404, API entity detail page Definition tab broken
//   - catalogImportPlugin → /catalog-import 404 (UI to add catalog-info.yaml to repos)
//   - catalogGraphPlugin  → /catalog-graph 404 (entity relationship visualizer)
//
// 2026-07-29 Wave 1 plugin expansion (see docs/plugins/README.md): all nine
// below ship native `/alpha` frontend-system exports (verified against
// package.json `exports["./alpha"]` before adding — several READMEs were
// stale about this). GitHub cards/tabs read the existing GitHub App
// integration; no new secret. homeModule/techRadarModule are local
// composition modules (pattern: modules/secrets) supplying the actual
// HomePage layout and the tech-radar data API respectively.
//
// 2026-07-29 Wave 2 plugin expansion (see docs/plugins/README.md): ten more
// `/alpha` exports, re-verified against installed node_modules exports
// fields (not just READMEs) before adding. cicdStatisticsGithubModule is a
// FrontendModule (not a plugin) that supplies cicd-statistics' CI data
// source — both entries are required together or the CI/CD stats card has
// no backing API. managePlugin has NO `/alpha` export (its FrontendPlugin
// lives at the package root) — imported without the `/alpha` suffix,
// unlike every other Wave 1/2 plugin.
//
// 2026-07-29 Wave 3 (see docs/plugins/README.md): Tech Insights — native
// `/alpha` FrontendPlugin. Scorecard/checks content is driven entirely by
// the `techInsights:` config block (app-config.yaml); the 3 built-in
// fact-retrievers are config-gated — they collect nothing if not listed
// there.
//
// 2026-07-29 Wave 3, PR 2: Qeta — native `/alpha` FrontendPlugin (Q&A /
// knowledge base). Self-registers the /qeta page+nav, an entity Q&A tab,
// a search-result item, and a TechDocs "ask question" addon. Storage
// (attachments) is S3 in prod / database locally — see `qeta:` config
// block. RBAC-gated (see rbac-policy.csv).
//
// 2026-09-08 Wave 4 (see docs/plugins/README.md + docs/operations.md →
// "AWS Labs plugin suite"): AWS Labs suite. Unlike every plugin above,
// every `@aws/*` frontend package predates the new frontend system (no
// `/alpha` export) — costInsightsPlugin is the ONE native `/alpha` plugin
// in this wave (from `@backstage-community/plugin-cost-insights`, the
// core Backstage page); the AWS package only supplies an alternate
// `costInsightsApiRef` implementation (Cost Explorer instead of the
// default backend-proxy), wired in via awsCostInsightsModule. The other
// five (ecs/ecr/codepipeline/codebuild/securityhub) are legacy entity-page
// components bridged with `convertLegacyEntityCardExtension` /
// `convertLegacyEntityContentExtension` — see each module's own header
// comment. All read-only AWS-service visibility; installs and boots
// cleanly with empty results until DevOps attaches the IAM policy in
// docs/operations.md.
export default createApp({
  features: [
    catalogPlugin,
    scaffolderPlugin,
    techDocsPlugin,
    techDocsMermaidAddonModule,
    notificationsPlugin,
    signalsPlugin,
    rbacPlugin,
    searchPlugin,
    userSettingsPlugin,
    apiDocsPlugin,
    catalogImportPlugin,
    catalogGraphPlugin,
    githubPullRequestsBoardPlugin,
    githubActionsPlugin,
    githubIssuesPlugin,
    announcementsPlugin,
    homePlugin,
    techRadarPlugin,
    bookmarksPlugin,
    explorePlugin,
    entityValidationPlugin,
    cicdStatisticsPlugin,
    cicdStatisticsGithubModule,
    todoPlugin,
    npmPlugin,
    linguistPlugin,
    readmePlugin,
    toolboxPlugin,
    wheelOfNamesPlugin,
    entityFeedbackPlugin,
    devToolsPlugin,
    managePlugin,
    techInsightsPlugin,
    qetaPlugin,
    costInsightsPlugin,
    navModule,
    secretsModule,
    signInModule,
    homeModule,
    techRadarModule,
    awsEcsModule,
    awsEcrModule,
    awsCodePipelineModule,
    awsCodeBuildModule,
    awsSecurityHubModule,
    awsCostInsightsModule,
  ],
});
